<template lang="pug">
el-config-provider(:locale="elementPlusLocale")
  SmxBaseLayout
    template(#navbar)
      Navbar(          
        v-if="!isPublicPage"
        :post-logout="logoutCallback"
        title=""
        logo-path="/img/eule_smx_grey.svg"
        logo-path-small="/img/eule_smx_grey.svg"
        )
    template(#menu)
      SmxMenu(v-if="!isPublicPage")        
    .app-content(
      class="max-md:p-5 md:p-10")
      RouterView(:key="route.path")
</template>

<script setup lang="ts">
import { onMounted, watch, ref, provide } from 'vue'
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router'
import Navbar from '@shared/components/Header.vue'
import { useI18n } from 'vue-i18n'
import { publicPages } from '@shared/router/loginGuard'
import { useAuthStore } from '../../shared/src/stores/auth'
import { useAppStore } from '../../shared/src/stores/apps'
import SmxMenu from '@shared/components/SmxMenu.vue'
import { useMenuStore } from '../../shared/src/stores/menu'
import SmxBaseLayout from '@shared/views/SmxBaseLayout.vue'
import { useMyAccountStore } from '../../shared/src/stores/myAccount'
import { useKeyCloakAuthentication } from '../../shared/src/hooks/authentication'
import useMenu from '@shared/hooks/menu'
import { elementPlusLocale } from '@shared/util/languages'
import { SFW_NAME_RESOLVER_KEY, type SFWNameResolver } from '@shared/shopFloorWalk/constants'
import { initProductsApp } from '@shared/products/hooks/initProducts'
import type { AssetTypeTitles } from '@shared/components/twinManagement/twinTypeGrouping'

const myAccountStore = useMyAccountStore()
useKeyCloakAuthentication()
const { isPublicPage } = useMenu()
const components = {
  Navbar
}

// NOTE: we do not have guarantees on which customIds exist on a sfw entity,
// this is used to set the name of the tickets, in this case order-customer.
// The fallback is the name of the entity itself, so this should be set in the app that uses the sfw module

const sfwNameResolver: SFWNameResolver = (values, properties) => {
  const orderProp = properties.find((p) => p.customId === 'order')
  const customerProp = properties.find((p) => p.customId === 'customer')
  const order = orderProp ? values[orderProp.uuid] : undefined
  const customer = customerProp ? values[customerProp.uuid] : undefined
  if (
    typeof order === 'string' &&
    order !== '' &&
    typeof customer === 'string' &&
    customer !== ''
  ) {
    return `${order} - ${customer}`
  }
  return undefined
}
provide(SFW_NAME_RESOLVER_KEY, sfwNameResolver)

const assetTypeTitles: AssetTypeTitles = {
  'asset.produktion.robots': 'demoRobots'
}
provide('assetTypeTitles', assetTypeTitles)

const appStore = useAppStore()

const menuStore = useMenuStore()

const router = useRouter()

const logoutCallback = () => {
  router.push({ name: 'login' })
}

// const isPublicPage = computed(() => {

//   console.log(router.currentRoute.value.fullPath)
//   return
// });

const route = useRoute()
</script>

<style>
body {
  max-width: 100vw;
  overflow-x: hidden;
}

.app-body {
  display: flex;
}

.app-content {
  width: 100%;
}

:root {
  /* --el-color-primary: #468798;
  --el-color-primary-light-3: #3b7686;
  --el-color-primary-light-5: #306573;
  --el-color-primary-light-7: #265461;
  --el-color-primary-light-8: #1b434f;
  --el-color-primary-light-9: #112c36;
  --el-color-primary-dark-2: #7bb6c6; */

  --el-color-primary: #468798;
  --el-color-primary-light-3: #5b9cac;
  --el-color-primary-light-5: #7bb6c6;
  --el-color-primary-light-7: #a2cfdb;
  --el-color-primary-light-8: #c0dee6;
  --el-color-primary-light-9: #eaf6f9;
  --el-color-primary-dark-2: #356977;

  /* 
  --el-color-primary: #409eff;
  --el-color-primary-light-3: #3375b9;
  --el-color-primary-light-5: #2a598a;
  --el-color-primary-light-7: #213d5b;
  --el-color-primary-light-8: #1d3043;
  --el-color-primary-light-9: #18222c;
  --el-color-primary-dark-2: #66b1ff;
  --el-color-success: #67c23a;
  --el-color-success-light-3: #4e8e2f;
  --el-color-success-light-5: #3e6b27;
  --el-color-success-light-7: #2d481f;
  --el-color-success-light-8: #25371c;
  --el-color-success-light-9: #1c2518;
  --el-color-success-dark-2: #85ce61;
  --el-color-warning: #e6a23c;
  --el-color-warning-light-3: #a77730;
  --el-color-warning-light-5: #7d5b28;
  --el-color-warning-light-7: #533f20;
  --el-color-warning-light-8: #3e301c;
  --el-color-warning-light-9: #292218;
  --el-color-warning-dark-2: #ebb563;
  --el-color-danger: #f56c6c;
  --el-color-danger-light-3: #b25252;
  --el-color-danger-light-5: #854040;
  --el-color-danger-light-7: #582e2e;
  --el-color-danger-light-8: #412626;
  --el-color-danger-light-9: #2b1d1d;
  --el-color-danger-dark-2: #f78989;
  --el-color-error: #f56c6c;
  --el-color-error-light-3: #b25252;
  --el-color-error-light-5: #854040;
  --el-color-error-light-7: #582e2e;
  --el-color-error-light-8: #412626;
  --el-color-error-light-9: #2b1d1d;
  --el-color-error-dark-2: #f78989;
  --el-color-info: #909399;
  --el-color-info-light-3: #6b6d71;
  --el-color-info-light-5: #525457;
  --el-color-info-light-7: #393a3c;
  --el-color-info-light-8: #2d2d2f;
  --el-color-info-light-9: #202121;
  --el-color-info-dark-2: #a6a9ad; */
  /* --el-box-shadow: 0px 12px 32px 4px rgba(0, 0, 0, 0.36), 0px 8px 20px rgba(0, 0, 0, 0.72);
  --el-box-shadow-light: 0px 0px 12px rgba(0, 0, 0, 0.72);
  --el-box-shadow-lighter: 0px 0px 6px rgba(0, 0, 0, 0.72);
  --el-box-shadow-dark: 0px 16px 48px 16px rgba(0, 0, 0, 0.72), 0px 12px 32px #000000,
    0px 8px 16px -8px #000000; */
  /* --el-bg-color-page: #0a0a0a;
  --el-bg-color: #141414;
  --el-bg-color-overlay: #1d1e1f; */
  /* --el-text-color-primary: #e5eaf3;
  --el-text-color-regular: #cfd3dc;
  --el-text-color-secondary: #a3a6ad;
  --el-text-color-placeholder: #8d9095;
  --el-text-color-disabled: #6c6e72; */
  /*   
  --el-border-color-darker: #254b56;
  --el-border-color-dark: #356977;
  --el-border-color: #468798;
  --el-border-color-light: #7bb6c6;
  --el-border-color-lighter: #a2cfdb;
  --el-border-color-extra-light: #c0dee6;
   */
  /* --el-fill-color-darker: #424243;
  --el-fill-color-dark: #39393a;
  --el-fill-color: #303030;
  --el-fill-color-light: #262727;
  --el-fill-color-lighter: #1d1d1d;
  --el-fill-color-extra-light: #191919;
  --el-fill-color-blank: transparent; */
  /* --el-mask-color: rgba(0, 0, 0, 0.8);
  --el-mask-color-extra-light: rgba(0, 0, 0, 0.3); */
  --el-border: var(--el-border-width) var(--el-border-style) var(--el-border-color);
}
</style>
